import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthService } from '../services/auth/auth.service';
import { Observable } from 'rxjs';
import { openProtectedRoutes } from '../../../environments/environment';
import { UserAmilearnStatus } from '../services/user/user.type';
import { TokenService } from '../services/auth/token.service';
import { AppRoute, userStatusRoutingTable } from 'src/app/routing/routing.config';
import { NGXLogger } from 'ngx-logger';
import { ErrorService } from '../services/error/error.service';
import { AppErrorCode } from '../services/error/error.constants';
import { RoutingUtils } from '../../routing/routing.utils';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    private authService: AuthService,
    private router: Router,
    private tokenService: TokenService,
    private logger: NGXLogger,
    private errorService: ErrorService,
    private routingUtils: RoutingUtils,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    if (openProtectedRoutes(route.queryParamMap)) {
      return true;
    }
    return this.checkAccess(route);
  }

  private async checkAccess(route: ActivatedRouteSnapshot): Promise<boolean> {
    this.logger.info('[AuthGuard] checkAccess - route: ', route);
    this.logger.info(
      '[AuthGuard] checkAccess - routeConfig.path: ',
      route.routeConfig.path
    );
    let accessState = false;

    if (await this.authService.isAuthenticated()) {
      // return true;
      const tokenPayload = this.tokenService.idTokenPayload();
      if (tokenPayload) {
        accessState = this.canAccessRoute(tokenPayload.amilearnStatus, route);

        // Redirect to allowed route
        if (accessState === false) {
          const newRoute: AppRoute = this.routingUtils.routeForUserStatus(tokenPayload.amilearnStatus);

          this.logger.debug(`[AuthGuard] User is not eligible for route ${route.routeConfig.path}. Redirect to allowed router ${newRoute}`);

          this.router.navigate([newRoute]);

          return accessState;
        }
      }
    } else {
      if (route.routeConfig.path !== AppRoute.ERROR_PAGE) {
        this.router.navigate([AppRoute.WELCOME])

        return accessState;
      }
    }

    // Default state - navigate to Error
    if (accessState === false) {
      this.errorService.routeToAuthError(AppErrorCode.AUTH_ERROR);
    }

    return accessState;
  }

  private canAccessRoute(
    userStatus: UserAmilearnStatus,
    route: ActivatedRouteSnapshot
  ): boolean {

    this.logger.debug(
      `[AuthGuard] canAccessRoute() - userStatus: ${userStatus} route path: ${route.routeConfig.path} `
    );

    const allowedRoute = userStatusRoutingTable[userStatus];
    this.logger.debug('[AuthGuard] canAccessRoute() - allowedRoute: ', allowedRoute);

    return route.routeConfig.path === allowedRoute;
  }
}
